import React, { useEffect, useState, useRef } from 'react';
import useTreatmentAppointmentDetail from 'hooks/treatment/useTreatmentAppointmentDetail';
import { Drawer, Button, Tag, Image, Popover, Alert } from 'antd';
import { isImage } from 'components/Prescriptions/components/AttachFiles';
import { providerGetKlarityResourcesUrl } from 'api/provider';
import { FileUploadResult } from 'hooks/useFileUpload';
import s from './s.module.less';
import PrescriptionCard from 'components/PrescriptionCard';
import { formatUTCTime } from 'utils/common';
import { sanitizeRichHtml } from 'utils/sanitize';
import { getProviderNameDisplay } from 'utils/provider';
import ProviderStore from 'store/Provider';
import { TextVisitAttachment, IPaymentStatus } from 'types/textVisit';
import VisitCard from 'components/Prescriptions/components/VisitCard';
import RescheduleModal from 'components/RescheduleModal';
import { TServerCalendarAppointmentEventDetail } from 'types/calendar';
import { useExportPdf } from 'hooks/useExportPdf';
import PrintContent from 'components/VideoVisitUnSubmittedDrawer/components/PrintContent';
import usePrivateFilePreview from 'hooks/usePrivateFilePreview';
import { useReactToPrint } from 'react-to-print';
import { PlusOutlined } from '@ant-design/icons';
import AddendumsSection from './AddendumsSection';

interface VideoVisitSubmittedDrawerProps {
    show: boolean;
    onClose: () => void;
    appointmentId?: number;
}

const VideoVisitSubmittedDrawer = ({ show, onClose, appointmentId }: VideoVisitSubmittedDrawerProps) => {
    const [caseData, loading, fetchData] = useTreatmentAppointmentDetail(appointmentId);
    const [attachments, setAttachments] = useState<FileUploadResult[]>([]);
    const [scheduledVisit, setScheduledVisit] = useState<TServerCalendarAppointmentEventDetail>();
    const [showRescheduleModal, setShowRescheduleModal] = useState(false);
    const printContentRef = useRef<HTMLDivElement>(null);

    const downloadPdf = useExportPdf(printContentRef);
    const { previewUrl: providerSignaturePreviewUrl, loading: providerSignatureLoading } = usePrivateFilePreview(
        caseData?.providerSignature?.signatureFileKey,
    );

    const [getUser] = ProviderStore.useStore();
    const providerName = getProviderNameDisplay(getUser('data'));
    const providerCredentials = getUser('data')?.credential;
    const patientName =
        caseData?.patientFirstName && caseData?.patientLastName
            ? `${caseData.patientFirstName} ${caseData.patientLastName}`
            : 'Patient';
    useEffect(() => {
        if (show && appointmentId) {
            fetchData(appointmentId);
        }
    }, [show, appointmentId]);

    useEffect(() => {
        const fetchAttachmentUrls = async () => {
            try {
                const resAttachments: FileUploadResult[] = await Promise.all(
                    (caseData?.treatmentPlan?.attachments as TextVisitAttachment[])?.map(async (attachment) => {
                        const res = await providerGetKlarityResourcesUrl(attachment.path || '');
                        if (!res.error) {
                            return {
                                path: attachment.path,
                                name: attachment.name,
                                type: attachment.type,
                                previewUrl: res.data?.data?.singedUrl || '',
                            };
                        }
                        return attachment;
                    }),
                );
                setAttachments(resAttachments);
            } catch (error) {
                console.error(error);
            }
        };
        if (caseData?.treatmentPlan?.attachments) {
            fetchAttachmentUrls();
        }
    }, [caseData?.treatmentPlan?.attachments]);

    const handlePrint = useReactToPrint({
        contentRef: printContentRef,
        documentTitle: 'Visit Notes',
    });
    // const handleDownload = () => {
    //     downloadPdf('Treatment Plan');
    // };

    if (!caseData) return null;

    const title = ['Video visit', caseData.serviceSpecialty, caseData.serviceType].filter(Boolean).join(': ');

    const renderTreatmentPlan = () => {
        const data = caseData.treatmentPlan;
        if (!data) return null;

        return (
            <div className={s.treatmentPlan}>
                {/* Response Section */}
                <div className={s.section}>
                    <div className={s.sectionTitle}>Response</div>
                    <div className={s.responseContent}>
                        <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(data.treatmentPlan || '') }} />

                        {attachments?.length && attachments.length > 0 ? (
                            <div className={s.attachments}>
                                {attachments.map((attachment, index) => (
                                    <div key={index} className={s.attachmentItem}>
                                        {isImage(attachment) ? (
                                            <div className={s.attachmentPreview}>
                                                <Image
                                                    src={attachment.previewUrl}
                                                    alt={attachment.name}
                                                    className={s.filePreviewImage}
                                                    preview={{ src: attachment.previewUrl }}
                                                />
                                            </div>
                                        ) : (
                                            <div
                                                className={s.fileInfo}
                                                onClick={() => window.open(attachment.previewUrl || '', '_blank')}
                                            >
                                                <div className={s.fileName}>{attachment.name || ''}</div>
                                                <div className={s.fileType}>
                                                    {attachment.type
                                                        ? attachment.name?.split('.').pop()?.toUpperCase()
                                                        : 'Unknown type'}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>
                </div>

                {/* Prescription Section */}
                {caseData.prescriptions && caseData.prescriptions.length > 0 && (
                    <div className={s.section}>
                        <div className={s.sectionTitle}>Prescription</div>
                        <div className={s.prescriptionCards}>
                            {caseData.prescriptions.map((prescription, index) => (
                                <PrescriptionCard key={index} prescription={prescription} />
                            ))}
                        </div>
                    </div>
                )}
                {/* Private notes Section */}
                {data.providerNote && (
                    <div className={s.section}>
                        <div className={s.sectionTitle}>Private notes</div>
                        <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(data.providerNote || '') }} />
                    </div>
                )}
                <div className={s.providerViewOnlyWrap}>
                    <h4>The following session is not visible to patient</h4>
                    <div className={s.section}>
                        <div className={s.sectionTitle}>Notes for the Klarity billing team (optional)</div>
                        {data.klarityBillingNote ? (
                            <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(data.klarityBillingNote) }} />
                        ) : (
                            <div>-</div>
                        )}
                    </div>

                    <div className={s.section}>
                        <div className={s.sectionTitle}>Diagnosis</div>
                        {data.diagnoses && data.diagnoses.length > 0 ? (
                            <ul className={s.diagnosisList}>
                                {data.diagnoses.map(
                                    (entry: { diagnosisCode?: string; description?: string }, idx: number) => (
                                        <li key={idx}>
                                            {entry.diagnosisCode
                                                ? `${entry.diagnosisCode} - ${entry.description || ''}`
                                                : entry.description || '-'}
                                        </li>
                                    ),
                                )}
                            </ul>
                        ) : (
                            <div>-</div>
                        )}
                    </div>

                    <div className={s.section}>
                        <div className={s.sectionTitle}>Billing codes</div>
                        {data.billingCodes && data.billingCodes.length > 0 ? (
                            <div className={s.billingCodesList}>
                                <ul className={s.billingCodesUl}>
                                    {data.billingCodes.map(
                                        (
                                            entry: {
                                                cptCode?: string;
                                                description?: string;
                                                unitBasis?: string;
                                                units?: number;
                                                insuranceRate?: number;
                                                isPrimary?: boolean;
                                            },
                                            idx: number,
                                        ) => (
                                            <li key={idx}>
                                                <div className={s.billingCodeRow}>
                                                    <span className={s.billingCodeName}>
                                                        {entry.cptCode || '-'} — {entry.description || '-'}
                                                    </span>
                                                    <span className={s.billingCodeTag}>
                                                        <Tag className={entry.isPrimary ? s.primaryTag : s.addOnTag}>
                                                            {entry.isPrimary ? 'Primary' : 'Add-on'}
                                                        </Tag>
                                                    </span>
                                                    <span className={s.billingCodeUnit}>
                                                        {{
                                                            TIMED: 'Timed',
                                                            PER_ITEM_ADMINISTRATION: 'Per item/administration',
                                                            PER_SESSION: 'Per session',
                                                        }[entry.unitBasis || 'PER_SESSION'] || entry.unitBasis}
                                                    </span>
                                                    <span className={s.billingCodeUnit}>
                                                        {entry.units || 1} Unit{(entry.units || 1) > 1 ? 's' : ''}
                                                    </span>
                                                    <span className={s.billingCodeRate}>
                                                        {entry.insuranceRate != null
                                                            ? `$${entry.insuranceRate.toFixed(2)}`
                                                            : '-'}
                                                    </span>
                                                </div>
                                            </li>
                                        ),
                                    )}
                                </ul>
                                <div className={s.claimTotal}>
                                    Claim total{' '}
                                    <strong>
                                        $
                                        {data.billingCodes
                                            .reduce(
                                                (sum: number, e: { insuranceRate?: number; units?: number }) =>
                                                    sum + (e.insuranceRate || 0) * (e.units || 1),
                                                0,
                                            )
                                            .toFixed(2)}
                                    </strong>
                                </div>
                            </div>
                        ) : (
                            <div>-</div>
                        )}
                    </div>
                </div>

                <div className={s.section}>
                    <div className={s.sectionTitle}>Signatures</div>
                    <div className="flex gap-1 flex-col">
                        <img src={providerSignaturePreviewUrl} alt="Provider signature" />
                        <div>
                            Signed by {providerName}, {providerCredentials}
                        </div>
                        <div>{formatUTCTime(caseData.providerTreatmentSubmittedAt || '', 'MM/DD/YYYY, hh:mm A')}</div>
                    </div>
                </div>
                <AddendumsSection treatmentId={caseData.treatmentPlan.id} businessId={appointmentId!} />
                {caseData.upcomingVisitAppointments && caseData.upcomingVisitAppointments.length > 0 && (
                    <div className={s.visitCards}>
                        {caseData.upcomingVisitAppointments.map((visit) => (
                            <VisitCard
                                key={visit.id}
                                visit={visit}
                                onReschedule={() => {
                                    setScheduledVisit(visit);
                                    setShowRescheduleModal(true);
                                }}
                                header={<div className="text-gray-500 font-medium mb-2">Upcoming visit</div>}
                                showClientInfo
                                patientName={patientName}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <Drawer
                open={show}
                onClose={onClose}
                title={
                    <div className={s.header}>
                        <span className="text-base font-medium">{title}</span>
                        <div className="flex items-center gap-2">
                            <Popover
                                content="Printing only includes the text of the visit notes. Attachments must be downloaded individually."
                                overlayStyle={{ width: 300 }}
                            >
                                <Button type="text" style={{ padding: 0 }} onClick={handlePrint}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <path
                                            d="M17 17H19C20.1046 17 21 16.1046 21 15V11C21 9.89543 20.1046 9 19 9H5C3.89543 9 3 9.89543 3 11V15C3 16.1046 3.89543 17 5 17H7M9 21H15C16.1046 21 17 20.1046 17 19V15C17 13.8954 16.1046 13 15 13H9C7.89543 13 7 13.8954 7 15V19C7 20.1046 7.89543 21 9 21ZM17 9V5C17 3.89543 16.1046 3 15 3H9C7.89543 3 7 3.89543 7 5V9H17Z"
                                            stroke="#111827"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </Button>
                            </Popover>
                            {/* <Popover content="Downloading only includes the text of the visit notes. Attachments must be downloaded individually." overlayStyle={{ width: 300 }}>
                                <Button type="text" style={{ padding: 0 }} onClick={handleDownload}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M4 16L4 17C4 18.6569 5.34315 20 7 20L17 20C18.6569 20 20 18.6569 20 17L20 16M16 12L12 16M12 16L8 12M12 16L12 4" stroke="#111827" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                </Button>
                            </Popover> */}
                            <Button type="text" onClick={onClose} style={{ padding: 0 }}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        d="M6 18L18 6M6 6L18 18"
                                        stroke="#111827"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </Button>
                        </div>
                    </div>
                }
                closeIcon={null}
                rootClassName={s.drawer}
                width={900}
            >
                <div className={s.contentInfo}>
                    <div className={s.title}>
                        [{patientName}] {caseData.serviceSpecialty}
                    </div>

                    <div className={s.statusTags}>
                        {caseData.paymentStatus && (
                            <Tag className={s.paidTag}>
                                {caseData.paymentStatus === IPaymentStatus.SUCCEEDED && 'Paid'}
                                {caseData.paymentStatus === IPaymentStatus.REFUNDED && 'Refunded'}
                                {caseData.paymentStatus === IPaymentStatus.REFUNDED_FAILED && 'Refund failed'}
                                {caseData.paymentStatus === IPaymentStatus.REFUNDED_PROCESSING && 'Refund processing'}
                            </Tag>
                        )}
                        <Tag className={`${s.tag} ${caseData.idVerified ? s.verifiedTag : s.unverifiedTag}`}>
                            {caseData.idVerified ? 'ID verified' : 'ID missing'}
                        </Tag>
                        <Tag className={`${s.tag} ${caseData.pharmacySelected ? s.verifiedTag : s.unverifiedTag}`}>
                            {caseData.pharmacySelected ? 'Pharmacy selected' : 'Pharmacy missing'}
                        </Tag>
                    </div>

                    <div className={s.timeTable}>
                        <div className={s.tableRow}>
                            <div className={s.tableCell}>
                                <div className={s.label}>Visit time</div>
                                <div className={s.value}>
                                    {formatUTCTime(caseData.startAt || '', 'MM/DD/YYYY, hh:mm A')}
                                </div>
                            </div>
                            <div className={s.tableCell}>
                                <div className={s.label}>Sent to patient on</div>
                                <div className={s.value}>
                                    {formatUTCTime(caseData.providerTreatmentSubmittedAt || '', 'MM/DD/YYYY, hh:mm A')}
                                </div>
                            </div>
                        </div>
                    </div>
                    {renderTreatmentPlan()}
                </div>
            </Drawer>
            <RescheduleModal
                open={showRescheduleModal}
                onReopen={() => setShowRescheduleModal(true)}
                onClose={() => setShowRescheduleModal(false)}
                onSuccess={async () => {
                    setShowRescheduleModal(false);
                    setScheduledVisit(undefined);
                    fetchData(appointmentId!);
                }}
                appointmentEventDetail={scheduledVisit!}
                targetClient={caseData?.prospect!}
            />
            <div className={s.printWrapper}>
                <PrintContent ref={printContentRef} data={caseData?.treatmentPlan} caseData={caseData} />
            </div>
        </>
    );
};

export default VideoVisitSubmittedDrawer;
